import 'whatwg-fetch';
import humps from 'humps';

import {
  showRequestFailureAlert,
  receiveBotResponses,
  onBotResponsesSaved,
  updateUserSaysEntities,
} from '@src/actions/ApiAction';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';
import { langaugeCodeKeyDecamelize } from '@src/utils';

import { MogamboRequest, authenticate, captureException } from './index';
import { mogamboRequestV2, getEncodedStr } from './utils';
import { BASE_URL } from '../const';

export const searchForEntities = (filters) => {
  const url = `${BASE_URL}/entities/search?${getEncodedStr(filters)}`;
  return mogamboRequestV2(url, 'GET');
};

export const getEntityData = (entityName, entityId) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const endPoint = isEntityV2Enabled
    ? `v2/${entityId}?version=3`
    : `${entityName}?version=3`;
  const url = `${BASE_URL}/entities/entity_data/${endPoint}`;
  const response = mogamboRequestV2(url, 'GET');
  return Promise.resolve(response);
};

export const searchDictionary = ({
  dictionaryId,
  dictionaryName,
  searchTerm,
  onlyEmptyVariants,
  page,
  pageSize,
}) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const dictionaryIdentifier = isEntityV2Enabled
    ? `v2/${dictionaryId}`
    : dictionaryName;
  const offset = pageSize * page;
  const url = `${BASE_URL}/entities/dictionary/search/${dictionaryIdentifier}?search_term=${searchTerm}&empty_variants_only=${onlyEmptyVariants}&size=${pageSize}&from=${offset}`;
  const response = mogamboRequestV2(url, 'GET');
  return Promise.resolve(response);
};

export const saveEntityData = (name, id, data) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const endPoint = isEntityV2Enabled
    ? `v2/${id || '/'}?version=3`
    : `${name}?version=3`;
  const url = `${BASE_URL}/entities/entity_data/${endPoint}`;
  const headers = {
    'x-haptik-bot-name': data.domain_name,
  };
  const response = mogamboRequestV2(url, 'POST', data, false, headers);
  return Promise.resolve(response);
};

export const getCRFData = (name, id) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const CRFIdentifier = isEntityV2Enabled ? `v2/${id}` : name;
  const url = `${BASE_URL}/entities/crf_data/${CRFIdentifier}`;
  const response = mogamboRequestV2(url, 'GET');
  return Promise.resolve(response);
};

export const saveCRFData = (name, id, data) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const CRFIdentifier = isEntityV2Enabled ? `v2/${id}` : name;
  const url = `${BASE_URL}/entities/crf_data/${CRFIdentifier}`;
  const response = mogamboRequestV2(url, 'POST', data);
  return Promise.resolve(response);
};

export const saveDictionaryData = (name, id, data, domain_name) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const dictionaryIdentifier = isEntityV2Enabled ? `v2/${id}` : name;
  const url = `${BASE_URL}/entities/dictionary/edit/${dictionaryIdentifier}`;
  const headers = {
    'x-haptik-bot-name': domain_name,
  };
  const response = mogamboRequestV2(url, 'POST', data, false, headers);
  return Promise.resolve(response);
};

export const addLanguageToDictionary = (name, id, data) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const dictionaryIdentifier = isEntityV2Enabled ? `v2/${id}` : name;
  const url = `${BASE_URL}/entities/dictionary/languages/${dictionaryIdentifier}`;
  const response = mogamboRequestV2(url, 'POST', data);
  return Promise.resolve(response);
};

export const copyEntity = ({ srcName, dstName, languageList, domainName }) => {
  const url = `${BASE_URL}/entities/copy/`;
  const response = mogamboRequestV2(url, 'POST', {
    src_entity_name: srcName,
    dst_entity_name: dstName,
    language_list: languageList,
    domain_name: domainName,
  });
  return Promise.resolve(response);
};

export const uploadDictionary = async (name, id, file) => {
  const isEntityV2Enabled = BotStore.getIsEntityV2Enabled();
  const dictionaryIdentifier = isEntityV2Enabled ? `v2/${id}` : name;
  const url = `${BASE_URL}/entities/dictionary/csv/${dictionaryIdentifier}`;
  const formData = new FormData();

  formData.append(name, file);

  const request = new Request(url, {
    method: 'POST',
    credentials: 'same-origin',
    mode: 'cors',
    cache: 'default',
    body: formData,
  });

  const response = await fetch(request);
  const json = await response.json();
  authenticate(json);

  return Promise.resolve(json);
};

export const getBotResponses = (stepId, callback, languageCode, isSuperman) => {
  let url = `${BASE_URL}/node/bot_responses/${stepId}/?version=3`;

  if (!isSuperman) {
    url = `${BASE_URL}/node/bot_responses/${stepId}/?lang_src=${languageCode}&lang_script=${languageCode}`;
  }

  const request = MogamboRequest(url, 'GET');
  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 200 &&
        contentType &&
        contentType.indexOf('application/json') !== -1
      ) {
        return response.json().then((json) => {
          authenticate(json);
          if (json.success) {
            const supportedLanguages = json.meta.domain.language_list;
            const camelizedResponses = humps.camelizeKeys(
              json.body,
              (key, convert) => {
                return langaugeCodeKeyDecamelize(key, convert);
              }
            );

            const updatedResponses = {};
            const botMeta = { ...json.meta };

            const hybridSources = CommonStore.getMetaTags()
              ?.hybridLanguageSources;
            const hybridSourceKeys = Object.keys(hybridSources) || [];

            if (isSuperman) {
              if (hybridSourceKeys.length > 0) {
                supportedLanguages.push(...hybridSourceKeys);
              }

              botMeta.domain.language_list = [...supportedLanguages];

              // Iterate through all supported languages and
              // create enclose all responses within respective languages
              // eg. {en: {...}, hi: {...}}
              supportedLanguages.forEach((item) => {
                const languageResponse = {};
                const source = hybridSources[item] ? hybridSources[item] : item;
                const script = item.split('|')[0];
                languageResponse.botSays =
                  camelizedResponses.botSays[source]?.[script] || {};

                languageResponse.entities =
                  camelizedResponses.entities[source]?.[script] || [];

                languageResponse.finalBotReply =
                  camelizedResponses.finalBotReply[source]?.[script] || {};

                languageResponse.delay =
                  camelizedResponses.delay[source]?.[script] || {};

                languageResponse.delay.time = camelizedResponses.delay.time;

                languageResponse.hasApiIntegration =
                  camelizedResponses.hasApiIntegration;

                languageResponse.nonMandatoryResponses =
                  camelizedResponses.nonMandatoryResponses;

                languageResponse.flushEntities =
                  camelizedResponses.flushEntities;

                updatedResponses[item] = languageResponse;
              });
            } else {
              updatedResponses[languageCode] = camelizedResponses;
            }

            receiveBotResponses(
              updatedResponses,
              botMeta,
              callback,
              languageCode
            );
          } else {
            showRequestFailureAlert('Unable to retrieve bot responses');
          }
        });
      }
      showRequestFailureAlert('Unable to retrieve bot responses');
      console.log(`Request Failed: ${url}`);
    })
    .catch(captureException);
};

export const saveBotResponses = (
  stepId,
  botResponses,
  callback,
  domainTitle,
  domainId
) => {
  // Intermittently POST Call happens without langSrc and langScript.
  // To avoid that this check has been added.
  if (!botResponses.langSrc) {
    callback(true);
    return;
  }
  const url = `${BASE_URL}/node/bot_responses/${stepId}/`;
  delete botResponses.updated;
  const snakedResponses = humps.decamelizeKeys(botResponses);
  const headers = {
    'x-haptik-bot-name': domainTitle,
  };
  const request = MogamboRequest(url, 'POST', snakedResponses, headers);
  fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 200 &&
        contentType &&
        contentType.indexOf('application/json') !== -1
      ) {
        return response.json().then((json) => {
          authenticate(json);
          if (json.success) {
            onBotResponsesSaved(json.body, callback);
          } else {
            callback(true);
          }
        });
      }
      // Added this to continue execution post failure as well.
      callback(true);
      console.log(`Request Failed: ${url}`);
    })
    .catch(captureException);
};

export const setUserSaysEntities = (updatedEntities) => {
  const url = `${BASE_URL}entities/update_node_entity_data/`;
  const request = MogamboRequest(url, 'POST', updatedEntities);
  return fetch(request)
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json().then((json) => {
          authenticate(json);
          updateUserSaysEntities();
        });
      }
      throw new Error('Step Entities API request failed!');
    })
    .catch(captureException);
};
